import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, User } from 'lucide-react';
import PatientFormDialog from '@/components/PatientFormDialog';

const Patients = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = patients.filter(p =>
        p.identite.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.identite.prenom.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(patients);
    }
  }, [searchTerm, patients]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
      setFilteredPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePatientCreated = () => {
    fetchPatients();
    setShowDialog(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Patients</h1>
          <p className="text-gray-600 mt-1">Gestion des dossiers patients</p>
        </div>
        <Button
          onClick={() => setShowDialog(true)}
          data-testid="add-patient-button"
          style={{
            background: 'linear-gradient(135deg, #0066cc 0%, #4a90e2 100%)',
            color: 'white'
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Patient
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Rechercher un patient..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-patients-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredPatients.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-1">Aucun patient trouvé</p>
              <p className="text-sm">Commencez par ajouter un nouveau patient</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="patients-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Nom</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Prénom</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Date de Naissance</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Téléphone</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Allergies</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPatients.map((patient) => (
                    <tr
                      key={patient.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/patients/${patient.id}`)}
                      data-testid={`patient-row-${patient.id}`}
                    >
                      <td className="py-3 px-4 font-medium text-gray-900">{patient.identite.nom}</td>
                      <td className="py-3 px-4 text-gray-700">{patient.identite.prenom}</td>
                      <td className="py-3 px-4 text-gray-600">{patient.identite.date_naissance}</td>
                      <td className="py-3 px-4 text-gray-600">{patient.identite.telephone}</td>
                      <td className="py-3 px-4">
                        {patient.allergies.length > 0 ? (
                          <span className="text-red-600 text-sm font-medium">
                            {patient.allergies.join(', ')}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">Aucune</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/patients/${patient.id}`);
                          }}
                          data-testid={`view-patient-${patient.id}`}
                        >
                          Voir
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PatientFormDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSuccess={handlePatientCreated}
      />
    </div>
  );
};

export default Patients;
